import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Save, X, Loader2 } from 'lucide-react';

interface AIPostDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSaveAndProceed: () => void;
  totalPosts: number;
  isLoadingSave?: boolean;
}

export const AIPostDraftModal: React.FC<AIPostDraftModalProps> = ({
  isOpen,
  onClose,
  onDiscard,
  onSaveAndProceed,
  totalPosts,
  isLoadingSave = false,
}) => {
  const { t } = useTranslation();
  const numberOfOtherPosts = totalPosts - 1;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true" 
          onClick={!isLoadingSave ? onClose : undefined} // Prevent closing if loading
        />

        {/* Modal panel */}
        <div className="relative bg-white rounded-xl border border-black shadow-xl w-full max-w-md p-6 sm:my-8 sm:max-w-lg sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-6 w-6" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('contentCreation.modals.aiPostDraft.title', 'Handle Other Generated Posts')}
              </h2>
            </div>
            {!isLoadingSave && (
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">{t('common.close', 'Close')}</span>
              </button>
            )}
          </div>

          <div className="text-center mb-6">
            <p className="text-sm text-gray-600">
              {t('contentCreation.modals.aiPostDraft.message',
                "You are about to edit 1 of {{totalPosts}} posts. What do you want to do with the {{count}} remaining post(s)?",
                { totalPosts, count: numberOfOtherPosts }
              )}
            </p>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-3 gap-3">
            <button
              type="button"
              onClick={onDiscard}
              disabled={isLoadingSave}
              className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('contentCreation.modals.aiPostDraft.discard', 'Discard Others & Edit')}
            </button>
            <button
              type="button"
              onClick={onSaveAndProceed}
              disabled={isLoadingSave}
              className="w-full sm:w-auto inline-flex justify-center items-center rounded-lg border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingSave ? (
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
              ) : (
                <Save className="-ml-1 mr-2 h-4 w-4" />
              )}
              {isLoadingSave 
                ? t('common.saving', 'Saving...') 
                : t('contentCreation.modals.aiPostDraft.saveAndEdit', 'Save Others as Drafts & Edit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 